import { useEffect } from 'react'
import { useQuery } from 'react-query'

import { authenticatedApi } from '../api'
import type { ControlCatalogResponse } from '../types/control-catalog.types'
import { setControlCatalog } from '@/constants/control-labels'

/**
 * useControlCatalog — Phase 137+.
 *
 * Fetches the bilingual catalog (controlKey + name + plainDescription) of all
 * active controls via `GET /companies/:companyId/controls`. Replaces the
 * static `CONTROL_LABELS` mirror in `frontend/src/constants/control-labels.ts`.
 *
 * The hook also POPULATES the module-scoped cache used by
 * `getControlLabel` / `getControlPlainDescription` (the sync API used in
 * many places that can't easily await a hook). Mount this hook once at app
 * shell level (e.g. inside `AuthGuard` or an effect-only component
 * `<ControlCatalogBootstrap />`) so the cache is hot before any feature
 * page renders. Until the cache is populated, the sync helpers fall back
 * to a `prettify(controlKey)` shape — never raw snake_case.
 */
export const useControlCatalog = (companyId: string | undefined) => {
  const query = useQuery<ControlCatalogResponse, Error>(
    ['control-catalog', companyId],
    async () => {
      const res = await authenticatedApi.get<ControlCatalogResponse>(`/companies/${companyId}/controls`)
      return res.data
    },
    {
      enabled: Boolean(companyId),
      staleTime: 1000 * 60 * 60, // 1h — control catalog rarely changes mid-session
      cacheTime: 1000 * 60 * 60,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  )

  // Populate the sync cache whenever the data arrives. Idempotent: same
  // payload produces the same map.
  useEffect(() => {
    if (query.data?.controls) {
      setControlCatalog(query.data.controls)
    }
  }, [query.data])

  return query
}
